/**
 * The typed fit-occlusion contract: chrome laid over a map declares which map
 * edge it covers and the registered clearance the framed subject keeps from
 * it. Every automatic fit (the workspace map, every stand map, a report hero)
 * reads the same declarations, so the subject is framed in the map area the
 * chrome leaves free.
 *
 * - `top | right | bottom | left`: chrome docked to that map edge.
 * - `panel`: a side panel that becomes a bottom sheet on a narrow map. It is
 *   a sheet when its layout box spans at least three quarters of the map's
 *   width, otherwise it covers the side its centre is on.
 *
 * Extents are read from the chrome's LAYOUT box (offsetWidth/offsetHeight,
 * placed at its untransformed position). CSS transforms never change a layout
 * box, so a panel sliding or scaling in reports its at-rest extent instead of
 * the frame it happens to be in mid-animation.
 */

export const FM_MAP_OCCLUSION_EDGES = ['top', 'right', 'bottom', 'left', 'panel'] as const;
export type FmMapOcclusionEdge = (typeof FM_MAP_OCCLUSION_EDGES)[number];

/**
 * Registered clearance (px) kept between the chrome and the framed subject.
 * `flush` for chrome that only frames the edge, `control` for a row of map
 * controls, `panel` for a card, panel or sheet whose reading should not touch
 * the subject's outline.
 */
export const FM_MAP_OCCLUSION_INSETS = Object.freeze({
  flush: 0,
  control: 12,
  panel: 20,
});
export type FmMapOcclusionInset = keyof typeof FM_MAP_OCCLUSION_INSETS;

export interface FmMapOcclusionAttributes {
  'data-fm-map-occlusion': FmMapOcclusionEdge;
  'data-fm-map-occlusion-inset': FmMapOcclusionInset;
}

/** The two data attributes a chrome element spreads to register its occlusion. */
export function fmMapOcclusion(
  edge: FmMapOcclusionEdge,
  inset: FmMapOcclusionInset,
): FmMapOcclusionAttributes {
  return { 'data-fm-map-occlusion': edge, 'data-fm-map-occlusion-inset': inset };
}

export const MAP_OCCLUSION_SELECTOR = '[data-fm-map-occlusion]';

/** Space kept on an edge no chrome covers. */
export const MAP_FIT_EDGE_GAP_PX = 20;

const SHEET_WIDTH_SHARE = 0.75;

export type MapEdgePadding = Record<'top' | 'right' | 'bottom' | 'left', number>;

export class FmMapOcclusionError extends Error {}

interface LayoutBox {
  left: number;
  top: number;
  right: number;
  bottom: number;
  width: number;
}

function isEdge(value: string): value is FmMapOcclusionEdge {
  return (FM_MAP_OCCLUSION_EDGES as readonly string[]).includes(value);
}

function isInset(value: string): value is FmMapOcclusionInset {
  return Object.prototype.hasOwnProperty.call(FM_MAP_OCCLUSION_INSETS, value);
}

function describe(element: HTMLElement): string {
  return element.dataset.testid || element.className || element.tagName.toLowerCase();
}

function declaration(element: HTMLElement): { edge: FmMapOcclusionEdge; inset: number } {
  const edge = element.dataset.fmMapOcclusion ?? '';
  const inset = element.dataset.fmMapOcclusionInset ?? '';
  if (!isEdge(edge)) {
    throw new FmMapOcclusionError(
      `Map occlusion "${describe(element)}" declares edge "${edge}"; the contract accepts ${FM_MAP_OCCLUSION_EDGES.join(', ')}.`,
    );
  }
  if (!isInset(inset)) {
    throw new FmMapOcclusionError(
      `Map occlusion "${describe(element)}" declares inset "${inset}"; register it through fmMapOcclusion(edge, inset).`,
    );
  }
  return { edge, inset: FM_MAP_OCCLUSION_INSETS[inset] };
}

function isDisplayed(element: HTMLElement): boolean {
  if (element.offsetWidth === 0 && element.offsetHeight === 0) return false;
  return window.getComputedStyle(element).visibility !== 'hidden';
}

/**
 * The element's layout box at its untransformed position. The rendered rect
 * is moved back by the element's own translation and resized to its layout
 * size around the same centre, which also undoes a scale about the default
 * centre origin.
 */
function layoutBox(element: HTMLElement): LayoutBox {
  const rect = element.getBoundingClientRect();
  const transform = window.getComputedStyle(element).transform;
  // An untransformed element is already at rest: the identity matrix moves nothing.
  const matrix = new DOMMatrixReadOnly(transform && transform !== 'none' ? transform : undefined);
  const centreX = rect.left + rect.width / 2 - matrix.e;
  const centreY = rect.top + rect.height / 2 - matrix.f;
  const width = element.offsetWidth;
  const height = element.offsetHeight;
  return {
    left: centreX - width / 2,
    top: centreY - height / 2,
    right: centreX + width / 2,
    bottom: centreY + height / 2,
    width,
  };
}

function overlaps(box: LayoutBox, map: DOMRect): boolean {
  return !(
    box.right <= map.left ||
    box.left >= map.right ||
    box.bottom <= map.top ||
    box.top >= map.bottom
  );
}

function resolvedEdge(
  edge: FmMapOcclusionEdge,
  box: LayoutBox,
  map: DOMRect,
): Exclude<FmMapOcclusionEdge, 'panel'> {
  if (edge !== 'panel') return edge;
  if (box.width >= map.width * SHEET_WIDTH_SHARE) return 'bottom';
  const centre = (box.left + box.right) / 2;
  return centre - map.left <= map.right - centre ? 'left' : 'right';
}

function occupied(edge: Exclude<FmMapOcclusionEdge, 'panel'>, box: LayoutBox, map: DOMRect) {
  if (edge === 'top') return box.bottom - map.top;
  if (edge === 'right') return map.right - box.left;
  if (edge === 'bottom') return map.bottom - box.top;
  return box.right - map.left;
}

/** Every registered occlusion element in the document; a sheet may portal outside the map's tree. */
export function mapOcclusionElements(root: ParentNode = document): HTMLElement[] {
  return [...root.querySelectorAll<HTMLElement>(MAP_OCCLUSION_SELECTOR)];
}

/**
 * Fit padding for the map in `container`: on each edge, the deepest registered
 * occlusion over the map plus its clearance, and at least the edge gap. An
 * occlusion that does not overlap the map (another specimen, a closed panel)
 * is ignored.
 */
export function readMapOcclusionPadding(container: HTMLElement): MapEdgePadding {
  const map = container.getBoundingClientRect();
  const padding: MapEdgePadding = {
    top: MAP_FIT_EDGE_GAP_PX,
    right: MAP_FIT_EDGE_GAP_PX,
    bottom: MAP_FIT_EDGE_GAP_PX,
    left: MAP_FIT_EDGE_GAP_PX,
  };
  mapOcclusionElements().forEach((element) => {
    const { edge, inset } = declaration(element);
    if (!isDisplayed(element)) return;
    const box = layoutBox(element);
    if (!overlaps(box, map)) return;
    const side = resolvedEdge(edge, box, map);
    padding[side] = Math.max(padding[side], occupied(side, box, map) + inset);
  });
  return padding;
}

/** Smallest free fit region (px per axis) a padding may leave. */
const MIN_FIT_EXTENT_PX = 24;

/**
 * Scale one axis' padding down so a positive fit region always remains.
 * Heavy chrome on a narrow map can nominally exceed the map; Leaflet would
 * then compute a NaN zoom from a non-positive fit region. A not-yet-laid-out
 * axis keeps a quarter of its extent per side so the region is never negative.
 */
export function clampMapPaddingAxis(
  leading: number,
  trailing: number,
  extent: number,
): [number, number] {
  const budget = extent - MIN_FIT_EXTENT_PX;
  if (budget <= 0) {
    const safe = Math.max(0, extent / 4);
    return [safe, safe];
  }
  const total = leading + trailing;
  if (total <= budget) return [leading, trailing];
  const scale = budget / total;
  return [leading * scale, trailing * scale];
}

/** Occlusion padding for the map in `container`, clamped to leave a free fit region. */
export function mapFitPadding(container: HTMLElement): MapEdgePadding {
  const padding = readMapOcclusionPadding(container);
  const [left, right] = clampMapPaddingAxis(padding.left, padding.right, container.clientWidth);
  const [top, bottom] = clampMapPaddingAxis(padding.top, padding.bottom, container.clientHeight);
  return { top, right, bottom, left };
}
